import React, { useMemo } from 'react'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { RISK_TREATMENT } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  ActionGridColumn,
  GridColumn,
  GridRow,
} from 'src/components/Reusable/Grid'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip'
import { generatePermalink } from 'src/components/PageLayout/Permalink'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { DescriptionMarkup } from 'src/pages/Controls/IndexItem/DescriptionMarkup'
import { ControlModelId } from 'src/pages/Controls/IndexItem/ControlsListItem.components'
import { TitleText } from 'src/components/Reusable/Text/Title'
import { Pill } from 'src/components/Reusable/Pill'
import palette from 'src/designSystem/variables/palette'
import { PolicyGridColumn, RiskPillValue } from '../risks.styles'
import { RiskControlRowMenu } from '../menus'
import { RiskControl } from '../risks.constants'

export const RisksControlsGridRow = ({
  riskcontrol,
}: {
  riskcontrol: RiskControl
}): JSX.Element | null => {
  const controlIds = riskcontrol.risk.getControlIds()
  const controlIdsArr = useMemo(() => controlIds.split(','), [controlIds])
  const perControlPercentage = useMemo(() => {
    if (controlIdsArr.length === 0) return '0%'
    return `${parseFloat(((1 / controlIdsArr.length) * 100).toFixed(2))}%`
  }, [controlIdsArr])
  const isMitigated = useMemo(() => {
    return riskcontrol.risk.getTreatment() === RISK_TREATMENT.MITIGATE
  }, [riskcontrol.risk])

  const params = useParams()
  const permalink = useMemo(
    () =>
      generatePermalink({
        pageContext: params.pageContext as string,
        modelType: MODEL_TYPE.CONTROL,
        modelId: riskcontrol.id,
        isInternalLink: true,
      }),
    [params.pageContext, riskcontrol.id],
  )

  const controlName = useMemo(
    () => riskcontrol.control.getName(),
    [riskcontrol.control],
  )

  return (
    <GridRow
      id={`risks-controls-row-${riskcontrol.id}`}
      as={Link}
      to={permalink}
    >
      <GridColumn>
        <DepartmentChip departmentId={riskcontrol.dept} />
      </GridColumn>
      <GridColumn>
        <ControlModelId modelId={riskcontrol.control.getModelId()} />
      </GridColumn>
      <PolicyGridColumn>
        <Tooltip
          id={`controls-index-row-tooltip-${riskcontrol.id}`}
          placement={TooltipPositions.right}
          overlayType={TooltipOverlayType.popover}
          tooltipBody={
            <DescriptionMarkup objective={riskcontrol.control.getObjective()} />
          }
        >
          <TitleText>{controlName}</TitleText>
        </Tooltip>
      </PolicyGridColumn>
      <GridColumn>
        {riskcontrol.control_effectiveness ? (
          <Pill
            label={
              <RiskPillValue>
                {'Success' +
                  (isMitigated
                    ? `: ${perControlPercentage} risk reduction`
                    : '')}
              </RiskPillValue>
            }
            backgroundColor={palette.green[100]}
            $width={isMitigated ? '210px' : '70px'}
          />
        ) : (
          <Pill
            label={
              <RiskPillValue>
                {'Failure' + (isMitigated ? ': no risk reduction' : '')}
              </RiskPillValue>
            }
            backgroundColor={palette.magenta[100]}
            $width={isMitigated ? '210px' : '70px'}
          />
        )}
      </GridColumn>
      <ActionGridColumn>
        <RiskControlRowMenu
          riskId={riskcontrol.risk.getId()}
          controlId={riskcontrol.id}
          name={controlName}
        />
      </ActionGridColumn>
    </GridRow>
  )
}
